import React, {FormEvent, useEffect, useState} from 'react';
import Toggle from "components/Form/Toggle";
import Field from "components/Form/Field";
import {FormikHelpers} from "formik";
import {SponsorshipFilter as SponsorshipFilterSpec} from "types/AudienceCriteria";
import {setFilterDates} from "components/Form/CriteriaBuilder/Filters/Filter";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import SponsorshipStatus from "../../../../types/SponsorshipStatus";
import {useOperator} from "../../../../context/OperatorContext";
import {Option, SingleCombobox} from "../../Form/Control";
import useSponsorableOptions, {sponsorableAsOption} from "../../../../hooks/useSponsorableOptions";
import {plural} from "pluralize";
import {toCamelCase} from "../../../../services/string";
import SponsorableType from "../../../../types/SponsorableType";

// Helpers ----

async function fetchSponsorable(type: SponsorableType, id: number) {
  const resource = toCamelCase(plural(type)) as keyof Pick<typeof ngage, 'sites' | 'zones' | 'facilities' | 'exhibits' | 'elements'>
  const sponsorable = await ngage[resource].get(id)

  return sponsorable ? sponsorableAsOption(sponsorable, type) : null
}

const sponsorshipFilterTemplate: SponsorshipFilterSpec = {
  not: false,
  tier_id: 0,
  status: "any",
  sponsorable_type: null,
  sponsorable_id: null,
  mode: "ever",
  dates: [],
}

// Component ----

export type SponsorshipFilterProps = {
  formik: FormikHelpers<any>
  groupIndex: number
  filter?: SponsorshipFilterSpec | null
}

function SponsorshipFilter({formik, groupIndex, filter}: SponsorshipFilterProps) {
  const {termFor} = useOperator()

  const [selectedSponsorable, setSelectedSponsorable] = useState<Option | null>({id: null, name: "any sponsorable"})

  useEffect(() => {
    if (!!filter?.sponsorable_type && !!filter.sponsorable_id) {
      fetchSponsorable(filter.sponsorable_type, filter.sponsorable_id).then(setSelectedSponsorable)
    }
  }, [filter?.sponsorable_type, filter?.sponsorable_id])

  const {data: tiers, isLoading: tiersLoading} = useQuery(
    ['sponsorship-tiers.all', {page: 1, perPage: 1000}],
    () => ngage.sponsorshipTiers.all(1)
  )

  const [sponsorablesSearchTerm, setSponsorablesSearchTerm] = useState<string>()
  const {data: sponsorables, isLoading: sponsorableLoading} = useSponsorableOptions(sponsorablesSearchTerm)

  // Handle toggling this filter on and off
  function handleToggle(checked: boolean): void {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.sponsorship`,
      checked ? {...sponsorshipFilterTemplate} : undefined
    )
  }

  // Handling changing the "not" state
  function handleChangeNot(e: FormEvent<HTMLSelectElement>) {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.sponsorship.not`,
      e.currentTarget.value === 'true'
    )
  }

  // Handle changing the sponsorable selected
  function handleSelectSponsorable(sponsorable: Option | null) {
    void formik.setFieldValue(`criteria.groups.${groupIndex}.filters.sponsorship.sponsorable_type`, sponsorable?.type ?? null)
    void formik.setFieldValue(`criteria.groups.${groupIndex}.filters.sponsorship.sponsorable_id`, sponsorable?.id ?? null)
    setSelectedSponsorable(sponsorable ?? {id: null, name: 'any sponsorable'})
  }

  // Handling changing the date mode
  function handleChangeMode(e: FormEvent<HTMLSelectElement>) {
    void formik.setFieldValue(`criteria.groups.${groupIndex}.filters.sponsorship.mode`, e.currentTarget.value)

    setFilterDates(formik,
      `criteria.groups.${groupIndex}.filters.sponsorship.dates`,
      e.currentTarget.value,
      filter?.dates || []
    )
  }

  return (
    <div className="mt-8">
      <div className="flex items-center">
        <Toggle
          label={termFor('Sponsorship')}
          checked={!!filter}
          onChange={handleToggle}
          loading={tiersLoading || sponsorableLoading}
          disabled={tiersLoading || sponsorableLoading}
        />

        {!filter && <span className="font-medium">...</span>}
      </div>

      {!!filter &&
        <div>
          <div className="mt-2 pl-14 lg:flex items-center">

            {/* Not */}
            <div className="flex-shrink-0">
              <Field
                as="select"
                name={`criteria.groups.${groupIndex}.filters.sponsorship.not`}
                disabled={!filter}
                value={filter.not ? 'true' : 'false'}
                onChange={handleChangeNot}
              >
                <option value="false">Has</option>
                <option value="true">Doesn't have</option>
              </Field>
            </div>

            {/* Tier */}
            <div className="mt-2 lg:mt-0 lg:w-48 lg:ml-3 flex-shrink-0">
              <Field
                as="select"
                name={`criteria.groups.${groupIndex}.filters.sponsorship.tier_id`}
                disabled={!filter}
              >
                <option value="0" key={0}>any tier</option>
                {tiers?.data.map(sponsorshipTier => (
                  <option value={sponsorshipTier.id} key={sponsorshipTier.id}>
                    {sponsorshipTier.name}
                  </option>
                ))}
              </Field>
            </div>

            <span className="ml-1 lg:mt-0 lg:ml-3 flex-shrink-0">
              {termFor('sponsorship')} for
            </span>

            {/* Sponsorable */}
            <div className="lg:ml-3 flex-shrink-0 basis-72">
              <SingleCombobox
                options={[{id: null, name: "any sponsorable"}, ...(sponsorables ?? [])]}
                selected={selectedSponsorable}
                onChange={handleSelectSponsorable}
                onSearch={setSponsorablesSearchTerm}
                isLoading={sponsorableLoading}
              />
            </div>
          </div>

          <div className="mt-2 pl-14 lg:flex items-center">
            {/* Status */}
            <div className="mt-2 lg:mt-0 flex-shrink-0">
              <Field
                as="select"
                name={`criteria.groups.${groupIndex}.filters.sponsorship.status`}
                disabled={!filter}
              >
                <option value="any">with any status</option>
                <option value={SponsorshipStatus.STARTING}>starting</option>
                <option value={SponsorshipStatus.ENDING}>ending</option>
              </Field>
            </div>

            {/* Mode */}
            <div className="mt-2 lg:mt-0 lg:ml-3 flex-shrink-0">
              {filter?.status !== 'any' &&
                <Field
                  as="select"
                  name={`criteria.groups.${groupIndex}.filters.sponsorship.mode`}
                  disabled={!filter}
                  onChange={handleChangeMode}
                >
                  <option value="ever">ever</option>
                  <option value="today">today</option>
                  <option value="tomorrow">tomorrow</option>
                  <option value="yesterday">yesterday</option>
                  <option value="this-week">this week</option>
                  <option value="next-week">next week</option>
                  <option value="last-week">last week</option>
                  <option value="this-month">this month</option>
                  <option value="next-month">next month</option>
                  <option value="last-month">last month</option>
                  <option value="on">on</option>
                  <option value="between">between</option>
                  <option value="before">before</option>
                  <option value="after">after</option>
                </Field>
              }
            </div>

            {/* Date 1 */}
            <div className="mt-2 lg:mt-0 lg:ml-3 flex-shrink-0">
              {['on', 'before', 'after', 'between'].includes(filter?.mode || '') &&
                <Field
                  type="date"
                  name={`criteria.groups.${groupIndex}.filters.sponsorship.dates.0`}
                />
              }
            </div>

            {/* Date 2 */}
            <div className="lg:ml-3 lg:flex items-center flex-shrink-0">
              {['between'].includes(filter?.mode || '') &&
                <>
                  <span className="ml-1 lg:ml-0 lg:mr-3">and</span>
                  <Field
                    type="date"
                    name={`criteria.groups.${groupIndex}.filters.sponsorship.dates.1`}
                  />
                </>
              }
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export default SponsorshipFilter;